import { useEffect, useRef, useState } from 'react'
import { useQueryClient, type QueryKey } from '@tanstack/react-query'

export type WhatsApiWsStatus = 'connected' | 'reconnecting' | 'disconnected'

export interface UseWhatsApiWsResult {
  wsStatus: WhatsApiWsStatus
  lastEventAt: Date | null
}

const BACKOFF_MS = [1000, 2000, 4000, 8000, 16000, 32000, 60000] as const

const INVALIDATING_EVENTS = new Set([
  'message.sent',
  'message.failed',
  'message.deleted',
])

const NO_RECONNECT_CLOSE_CODES = new Set([4001, 4003])
const MANUAL_CLOSE_CODE = 4000

export function useWhatsApiWs(
  tenantId: number | string | null | undefined,
  queryKey: QueryKey,
): UseWhatsApiWsResult {
  const queryClient = useQueryClient()
  const [wsStatus, setWsStatus] = useState<WhatsApiWsStatus>('disconnected')
  const [lastEventAt, setLastEventAt] = useState<Date | null>(null)

  const queryKeyRef = useRef<QueryKey>(queryKey)
  queryKeyRef.current = queryKey

  useEffect(() => {
    if (!tenantId) return

    const token = localStorage.getItem('session_token') || ''
    if (!token) return

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const wsUrl = `${protocol}//${window.location.host}/api/v1/tenants/${tenantId}/whatsapi/ws?token=${token}`

    let ws: WebSocket | null = null
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null
    let attempt = 0
    let cancelled = false

    const connect = () => {
      if (cancelled) return
      ws = new WebSocket(wsUrl)

      ws.onopen = () => {
        if (cancelled) return
        attempt = 0
        setWsStatus('connected')
      }

      ws.onmessage = (event) => {
        if (cancelled) return
        try {
          const msg = JSON.parse(event.data as string) as { event?: string }
          setLastEventAt(new Date())
          if (msg.event && INVALIDATING_EVENTS.has(msg.event)) {
            queryClient.invalidateQueries({ queryKey: queryKeyRef.current })
          }
        } catch {
          // Ignore malformed messages
        }
      }

      ws.onerror = () => {
        ws?.close()
      }

      ws.onclose = (evt) => {
        if (cancelled) return
        if (NO_RECONNECT_CLOSE_CODES.has(evt.code)) {
          setWsStatus('disconnected')
          return
        }
        setWsStatus('reconnecting')
        const delay = BACKOFF_MS[Math.min(attempt, BACKOFF_MS.length - 1)]
        attempt += 1
        reconnectTimer = setTimeout(connect, delay)
      }
    }

    connect()

    return () => {
      cancelled = true
      if (reconnectTimer) clearTimeout(reconnectTimer)
      if (ws) {
        ws.onopen = null
        ws.onmessage = null
        ws.onerror = null
        ws.onclose = null
        ws.close(MANUAL_CLOSE_CODE)
      }
    }
  }, [tenantId, queryClient])

  return { wsStatus, lastEventAt }
}
